import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, Shield, ShieldCheck, KeyRound } from "lucide-react";
import PtoBalancePanel from "@/components/pto/PtoBalancePanel";
import { useRuntimeEngines } from "@/lib/runtimeEngines";

export default function ProfilePage() {
  const { user, refreshMe } = useAuth();
  const engines = useRuntimeEngines();
  const [enrollment, setEnrollment] = useState(null); // {secret, otpauth_url, qr_png_base64}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [disablePw, setDisablePw] = useState("");

  const startEnroll = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll");
      setEnrollment(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code: code.trim() });
      setMsg("MFA enabled. Great work.");
      setEnrollment(null);
      setCode("");
      await refreshMe();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: disablePw });
      setMsg("MFA disabled.");
      setDisablePw("");
      await refreshMe();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="profile-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Settings</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Profile & security</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" value={user?.full_name || "—"} />
          <Field label="Email" value={user?.email} />
          <Field label="Timezone" value={user?.timezone || "UTC"} />
          <Field
            label="Roles"
            value={
              <div className="flex flex-wrap gap-1">
                {(user?.roles || []).map((r) => (
                  <Badge key={r} variant="secondary" className="rounded-full">{r}</Badge>
                ))}
              </div>
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className={`rounded-xl p-3 ${user?.mfa_enabled ? "bg-success/15 text-success" : "bg-accent text-secondary"}`}>
            {user?.mfa_enabled ? <ShieldCheck className="h-5 w-5" /> : <Shield className="h-5 w-5" />}
          </div>
          <div>
            <CardTitle>Two-factor authentication (TOTP)</CardTitle>
            <CardDescription>
              {user?.mfa_enabled
                ? "Enabled. You'll be asked for a 6-digit code every time you sign in."
                : "Recommended. Add a second factor via any authenticator app (1Password, Authy, Google Authenticator)."}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {msg && <Alert data-testid="mfa-msg"><AlertDescription>{msg}</AlertDescription></Alert>}
          {err && <Alert variant="destructive" data-testid="mfa-err"><AlertDescription>{err}</AlertDescription></Alert>}

          {!user?.mfa_enabled && !enrollment && (
            <Button onClick={startEnroll} disabled={busy} data-testid="mfa-enroll-start">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Enroll a device
            </Button>
          )}

          {enrollment && (
            <div className="space-y-4 rounded-xl border bg-accent/40 p-4" data-testid="mfa-enroll-panel">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                <img
                  src={`data:image/png;base64,${enrollment.qr_png_base64}`}
                  alt="TOTP QR"
                  className="h-40 w-40 rounded-lg border bg-white p-2"
                />
                <div className="space-y-2 text-sm">
                  <div className="font-medium">1. Scan with your authenticator app</div>
                  <div className="text-muted-foreground">Or paste this secret manually:</div>
                  <code className="block rounded border bg-white px-3 py-2 text-xs">{enrollment.secret}</code>
                </div>
              </div>
              <div className="max-w-xs space-y-2">
                <Label htmlFor="code">2. Enter the current 6-digit code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  data-testid="mfa-verify-input"
                />
              </div>
              <Button onClick={verify} disabled={busy || code.length < 6} data-testid="mfa-verify-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Turn on MFA
              </Button>
            </div>
          )}

          {user?.mfa_enabled && (
            <div className="space-y-3 rounded-xl border p-4">
              <div className="text-sm text-muted-foreground">To disable MFA, confirm your password.</div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  type="password"
                  placeholder="Current password"
                  value={disablePw}
                  onChange={(e) => setDisablePw(e.target.value)}
                  data-testid="mfa-disable-password"
                />
                <Button variant="destructive" onClick={disable} disabled={busy || !disablePw} data-testid="mfa-disable-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Disable MFA
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <KioskPinCard onUpdated={refreshMe} pinSet={!!user?.kiosk_pin_set} />

      {engines.pto && <PtoBalancePanel />}
    </div>
  );
}

function KioskPinCard({ onUpdated, pinSet }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async () => {
    setMsg(""); setErr("");
    if (pin.length !== 6) { setErr("PIN must be 6 digits."); return; }
    if (pin !== confirmPin) { setErr("PINs do not match."); return; }
    setBusy(true);
    try {
      await api.post("/me/kiosk-pin", { new_pin: pin, current_password: currentPw });
      setMsg(pinSet ? "Kiosk PIN updated." : "Kiosk PIN set. You can use it at any worksite tablet.");
      setPin(""); setConfirmPin(""); setCurrentPw("");
      await onUpdated();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="kiosk-pin-card">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <div className={`rounded-xl p-3 ${pinSet ? "bg-success/15 text-success" : "bg-accent text-secondary"}`}>
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <CardTitle>Tablet kiosk PIN</CardTitle>
          <CardDescription>
            {pinSet
              ? "You have a PIN set. Use it on any tablet at a worksite you're assigned to."
              : "Set a 6-digit PIN so you can punch in at a shared worksite tablet. Never share it."}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {msg && <Alert data-testid="kiosk-pin-msg"><AlertDescription>{msg}</AlertDescription></Alert>}
        {err && <Alert variant="destructive" data-testid="kiosk-pin-err"><AlertDescription>{err}</AlertDescription></Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="new-pin">New 6-digit PIN</Label>
            <Input
              id="new-pin"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              className="font-mono text-lg tracking-[0.35em]"
              data-testid="own-pin-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-pin">Confirm PIN</Label>
            <Input
              id="confirm-pin"
              inputMode="numeric"
              maxLength={6}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              className="font-mono text-lg tracking-[0.35em]"
              data-testid="own-pin-confirm"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cur-pw">Confirm your current password</Label>
          <Input
            id="cur-pw"
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            data-testid="own-pin-current-password"
          />
        </div>
        <Button onClick={submit} disabled={busy || pin.length !== 6 || pin !== confirmPin || !currentPw} data-testid="own-pin-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {pinSet ? "Update PIN" : "Set PIN"}
        </Button>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}
